import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  AlertCircle, BookOpen, Check, ChevronDown, Copy, Download, ExternalLink, FileText, Loader2,
  MessageSquare, Search, Trash2, ListChecks, ArrowRight,
} from 'lucide-react';
import BotAvatar from './BotAvatar';
import { PRIORITY_COMPANIES } from '../constants/companyLogos';
import { resolveChatTitle } from '../utils/chatSessions';

const MODEL_LABELS = {
  'gpt-5.1': 'GPT-5.1',
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o mini',
  'gpt-oss-120b': 'GPT-OSS 120B',
};

function modelLabel(id) {
  return MODEL_LABELS[id] || id;
}

function ModelSelector({ models, selectedModel, onSelectModel }) {
  if (!models || models.length === 0) return null;
  return (
    <div className="relative flex-shrink-0">
      <select
        value={selectedModel || ''}
        onChange={e => onSelectModel?.(e.target.value)}
        title="Model used to answer"
        className="appearance-none h-8 pl-3 pr-8 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md hover:bg-[#f6f7fa] focus:outline-none focus:border-[#1c4ed8] cursor-pointer transition"
      >
        {models.map(id => (
          <option key={id} value={id}>{modelLabel(id)}</option>
        ))}
      </select>
      <ChevronDown size={12} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#76829a]" />
    </div>
  );
}

const SUGGESTIONS = [
  {
    title: 'Risk summary',
    text: 'Summarize this week\'s high-risk alerts',
    icon: FileText,
    prompt: 'Summarize this week\'s high-risk alerts',
  },
  {
    title: 'Legal watch',
    text: 'What legal actions involve Adani Group?',
    icon: BookOpen,
    prompt: 'What legal actions involve Adani Group?',
  },
  {
    title: 'Regulatory',
    text: 'Show regulatory updates for Reliance Industries',
    icon: ListChecks,
    prompt: 'Show regulatory updates for Reliance Industries',
  },
  {
    title: 'Priorities',
    text: 'Which companies need immediate attention?',
    icon: ArrowRight,
    prompt: 'Which companies need immediate attention?',
  },
];

// Follow-up chips come from the backend now, generated per answer from what was
// actually just discussed. The three fixed ones that used to live here
// ("Explain key risks" / "Create team brief" / "What changed recently?") were
// identical on every turn, so they rarely matched what the analyst wanted next.
// Each suggestion carries `label` (chip text, also what the chat bubble shows),
// `question` (what gets sent), and an optional `intent` that steers retrieval.

function extractCitations(text) {
  const numbers = [...text.matchAll(/\[(\d+(?:\s*,\s*\d+)*)\]/g)]
    .flatMap(match => match[1].split(',').map(n => Number(n.trim())).filter(Boolean));
  return [...new Set(numbers)];
}

function cleanItemText(text) {
  return text
    .replace(/^(\d+\.|[-*])\s*/, '')
    // Drop the [n] markers, and with them the comma that only ever separated
    // two of them - stripping "[6], [8], [9]." without it left "periods,,." on
    // screen. A comma is only swallowed when another marker follows it, so
    // ordinary sentence commas after a citation survive.
    .replace(/\s*\[\d+(?:\s*,\s*\d+)*\](?:\s*,\s*(?=\[\d+))?\s*/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function splitRow(line) {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
}

function formatAnswer(answer) {
  const lines = String(answer || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let para = [];

  const flushPara = () => {
    if (!para.length) return;
    const raw = para.join(' ').trim();
    para = [];
    const item = { text: cleanItemText(raw), citationNumbers: extractCitations(raw) };
    if (item.text) blocks.push({ type: 'paragraph', ...item });
  };

  for (let idx = 0; idx < lines.length; idx += 1) {
    const line = lines[idx];
    const trimmed = line.trim();

    if (!trimmed) { flushPara(); continue; }

    // Markdown table: consecutive lines that start with "|".
    if (trimmed.startsWith('|')) {
      flushPara();
      const rows = [];
      while (idx < lines.length && lines[idx].trim().startsWith('|')) {
        if (!/^\|?\s*:?-{2,}/.test(lines[idx].trim())) rows.push(splitRow(lines[idx]));
        idx += 1;
      }
      idx -= 1;
      if (rows.length) blocks.push({ type: 'table', head: rows[0], rows: rows.slice(1) });
      continue;
    }

    // Headings: "## Title" or a line that is entirely **bold**.
    const heading = trimmed.match(/^#{1,4}\s+(.+)$/) || trimmed.match(/^\*\*([^*]+)\*\*:?$/);
    if (heading) {
      flushPara();
      blocks.push({ type: 'heading', text: cleanItemText(heading[1]).replace(/:$/, '') });
      continue;
    }

    const ordered = /^\d+\.\s/.test(trimmed);
    const bullet = /^[-*\u2022]\s/.test(trimmed);
    if (ordered || bullet) {
      flushPara();
      const item = { text: cleanItemText(trimmed), citationNumbers: extractCitations(trimmed) };
      if (!item.text) continue;
      const last = blocks[blocks.length - 1];
      if (last?.type === 'list' && last.ordered === ordered) last.items.push(item);
      else blocks.push({ type: 'list', ordered, items: [item] });
      continue;
    }

    para.push(trimmed);
  }
  flushPara();
  return blocks;
}

function renderInlineMarkdown(text) {
  const tokens = String(text || '').split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

  return tokens.map((token, index) => {
    if (/^\*\*[^*]+\*\*$/.test(token)) {
      return (
        <strong key={index} className="font-semibold text-[#0d1524] bg-[#e7edfc] rounded px-1">
          {token.slice(2, -2)}
        </strong>
      );
    }
    return token.replace(/\*\*/g, '');
  });
}

function sourceDomain(source) {
  const raw = source?.source_domain || source?.domain || source?.url || '';
  try {
    return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace(/^www\./, '');
  } catch {
    return String(raw).replace(/^www\./, '') || 'Source';
  }
}

function sourceTitle(source) {
  return source?.title || sourceDomain(source);
}

function sourceForCitation(sources, citationNumber) {
  if (!Array.isArray(sources) || !citationNumber) return null;
  return sources.find(source => Number(source?.n) === citationNumber) || sources[citationNumber - 1] || null;
}

function sourcesForPart(sources, citationNumbers) {
  return (citationNumbers || [])
    .map(number => sourceForCitation(sources, number))
    .filter((source, index, list) => source?.url && list.findIndex(item => item?.url === source.url) === index);
}

function InlineContent({ text, citationNumbers, sources }) {
  const inlineSources = sourcesForPart(sources, citationNumbers);
  return (
    <>
      {renderInlineMarkdown(text)}
      {inlineSources.map(source => (
        <a
          key={source.url}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          title={sourceTitle(source)}
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#e7edfc] px-2 py-0.5 text-xs font-medium text-[#1c4ed8] transition hover:brightness-95"
        >
          {source.n ? `[${source.n}]` : sourceDomain(source)}
          <ExternalLink size={10} />
        </a>
      ))}
    </>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-[13px] text-[#5b6780] hover:text-[#1c4ed8] flex items-center gap-1 transition"
    >
      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
    </button>
  );
}

function ConversationsPanel({
  sessions = [],
  activeChatId,
  onSelectChat,
  onDeleteChat,
  onAsk,
  search,
  setSearch,
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(s => resolveChatTitle(s).toLowerCase().includes(q));
  }, [sessions, search]);

  const groups = useMemo(() => {
    const startOfToday = new Date().setHours(0, 0, 0, 0);
    const today = filtered.filter(s => (s.createdAt || 0) >= startOfToday);
    const earlier = filtered.filter(s => (s.createdAt || 0) < startOfToday);
    return [['Today', today], ['Earlier', earlier]].filter(([, list]) => list.length > 0);
  }, [filtered]);

  return (
    <aside className="hidden lg:flex w-[260px] flex-shrink-0 flex-col border-r border-[#e1e5ec] bg-white">
      <div className="flex-shrink-0 p-4 pb-3">
        <h2 className="text-[13.5px] font-semibold text-[#0d1524]">Your research</h2>
        <div className="relative mt-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#76829a] pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search conversations"
            className="w-full h-8 pl-9 pr-3 text-[13px] bg-[#f6f7fa] border border-[#e1e5ec] rounded-md focus:outline-none focus:border-[#1c4ed8] focus:bg-white placeholder:text-[#76829a]"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-10 text-center">
            <MessageSquare size={20} className="text-[#76829a]" />
            <p className="mt-2 text-[13px] font-medium text-[#0d1524]">No conversations yet</p>
            <p className="text-xs text-[#76829a] mt-1 leading-relaxed">
              Ask about risks, companies, or alerts to start one.
            </p>
          </div>
        ) : groups.map(([label, list]) => (
          <div key={label}>
            <div className="px-1.5 pt-2 pb-1 text-xs font-medium text-[#5b6780]">{label}</div>
            {list.map(session => {
              const active = session.id === activeChatId;
              const asked = (session.turns || []).filter(turn => !turn?.greeting && turn?.question).length;
              return (
                <div
                  key={session.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectChat?.(session.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') onSelectChat?.(session.id);
                  }}
                  className={`group flex items-center gap-2 px-2.5 py-2 rounded-md border-l-2 cursor-pointer transition ${
 active ? 'bg-[#e7edfc] border-[#1c4ed8]' : 'border-transparent hover:bg-[#f6f7fa]'
 }`}
                >
                  <span className="flex-1 min-w-0">
                    <b className="block truncate text-[12.5px] font-medium text-[#0d1524]">{resolveChatTitle(session)}</b>
                    <small className="text-xs text-[#76829a]">
                      {asked ? `${asked} question${asked === 1 ? '' : 's'}` : 'No questions yet'}
                    </small>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDeleteChat?.(session.id); }}
                    title="Delete conversation"
                    aria-label="Delete conversation"
                    className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-[#76829a] opacity-0 group-hover:opacity-100 hover:text-[#bd2f2b] hover:bg-[#fae8e7] transition"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        ))}

        <div className="px-1.5 pt-4 pb-1 text-xs font-medium text-[#5b6780]">Start with a question</div>
        {SUGGESTIONS.slice(0, 3).map(item => (
          <button
            key={item.title}
            type="button"
            onClick={() => onAsk?.(item.prompt)}
            className="block w-full text-left px-2.5 py-2 rounded-md text-[12.5px] text-[#45516a] hover:bg-[#f6f7fa] transition"
          >
            {item.prompt}
          </button>
        ))}
      </div>
    </aside>
  );
}

function EmptyChat({ records = [], onAsk }) {
  // Start-of-conversation screen: a few starting points, using a company that is actually tracked.
  const company = PRIORITY_COMPANIES.find(name => records.some(r => r.company === name))
    || records.find(r => r.company)?.company
    || '';
  const starters = [
    { title: 'What changed today?', sub: 'A cited summary of new alerts', prompt: 'What changed in the last 24 hours?' },
    { title: 'Which stories rely on unverified sources?', sub: 'Find items that need a second source', prompt: 'Which recent stories rely on unverified sources and need a second source?' },
    { title: 'Summarise the high risks for the board', sub: 'Most recent, with citations', prompt: 'Summarize the high-risk alerts this week' },
    ...(company
      ? [{ title: `Build a timeline for ${company}`, sub: 'Chronology of the reporting', prompt: `Build a timeline of the recent reporting on ${company}` }]
      : []),
  ];

  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <h2 className="text-[26px] leading-tight font-semibold tracking-tight text-[#0d1524] text-center">
          Ask about your <span className="text-[#1c4ed8]">coverage</span>
        </h2>
        <p className="mt-2 text-sm text-[#45516a] text-center max-w-md leading-relaxed">
          Answers cite the alerts in your workspace. Pick a starting point or write your own question.
        </p>
        <div className="mt-7 w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-3">
          {starters.map(item => (
            <button
              key={item.title}
              type="button"
              onClick={() => onAsk(item.prompt)}
              className="rounded-lg border border-[#e1e5ec] bg-white px-4 py-4 text-center transition hover:border-[#1c4ed8] hover:bg-[#f6f7fa]"
            >
              <b className="block text-sm font-semibold text-[#0d1524] leading-snug">{item.title}</b>
              <span className="block mt-1 text-[13px] text-[#76829a]">{item.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AiPage({
  conversation, aiPageInput, setAiPageInput, askInConversation, resetConversation, records,
  aiModels = [], selectedAiModel, onSelectAiModel,
  chatSessions = [], activeChatId, onSelectChat, onDeleteChat, onNewChat,
  hideBotSlot = false,
}) {
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  // The greeting turn that seeds every new chat does not count: a fresh chat shows the starter screen.
  const hasConversation = conversation.some(turn => !turn.greeting);
  const [chatSearch, setChatSearch] = useState('');
  const [scope, setScope] = useState('');
  const scopeCompanies = useMemo(
    () => [...new Set(records.filter(r => r.entity_type !== 'person' && r.company).map(r => r.company))].sort((a, b) => a.localeCompare(b)),
    [records],
  );
  // When a company scope is chosen the model gets the company; the bubble still shows what was typed.
  const ask = (text) => (scope
    ? askInConversation(`Regarding ${scope}: ${text}`, undefined, text)
    : askInConversation(text));
  const activeSession = chatSessions.find(session => session.id === activeChatId);
  const activeTitle = hasConversation && activeSession ? resolveChatTitle(activeSession) : 'New conversation';

  const hasPendingTurn = conversation.some(turn => turn.loading);

  useEffect(() => {
    if (hasConversation) chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, hasConversation]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  }, [aiPageInput]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!aiPageInput.trim() || hasPendingTurn) return;
    ask(aiPageInput);
    setAiPageInput('');
  };

  const askFollowUp = (turn, followUp) => {
    askInConversation(followUp.question, undefined, followUp.label, followUp.intent);
  };

  const handleExport = () => {
    const lines = conversation
      .filter(turn => turn.question || turn.answer)
      .flatMap(turn => [
        turn.question ? `**You:** ${turn.displayQuestion || turn.question}` : null,
        turn.answer ? `**CorpSentinel AI:** ${turn.answer}` : null,
        ...(turn.sources || []).slice(0, 5).map((src, i) => `  [${src.n || i + 1}] ${sourceTitle(src)}${src.url ? ` - ${src.url}` : ''}`),
        '',
      ].filter(line => line !== null));
    const blob = new Blob([`# ${activeTitle}\n\n${lines.join('\n')}`], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'conversation.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Only call once — previously both onNewChat + resetConversation ran and
  // each created a session, so "New chat" / refresh stacked blank rows.
  const handleNewChat = () => {
    onNewChat?.();
  };

  return (
    <div className="h-full flex flex-col min-h-0 w-full">
      <div className="flex-shrink-0 flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl leading-tight font-semibold tracking-tight text-[#0d1524]">
            From information to understanding.
          </h1>
          <p className="hidden sm:block mt-1 text-sm text-[#45516a]">
            Research your portfolio with answers grounded in the reporting you monitor.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={!hasConversation}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Download size={14} />Export conversation
          </button>
          <button
            type="button"
            onClick={handleNewChat}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white hover:brightness-110 transition"
          >
            New conversation
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex bg-white border border-[#e1e5ec] rounded-lg overflow-hidden">
        <ConversationsPanel
          sessions={chatSessions}
          activeChatId={activeChatId}
          onSelectChat={onSelectChat}
          onDeleteChat={onDeleteChat}
          onAsk={askInConversation}
          search={chatSearch}
          setSearch={setChatSearch}
        />

        <div className="flex-1 min-w-0 flex flex-col">
          {/* Chat header */}
          <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-[18px] py-3 border-b border-[#e1e5ec]">
            <div className="min-w-0 flex items-center gap-2">
              <b className="text-[13px] font-semibold text-[#0d1524] truncate">{activeTitle}</b>
              <small className="hidden sm:inline text-xs font-medium bg-[#f6f7fa] border border-[#e1e5ec] rounded px-1.5 py-0.5 text-[#45516a] whitespace-nowrap">
                {records.length.toLocaleString()} records
              </small>
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-auto sm:mr-2">
              <label className="flex items-center gap-1.5 text-[13px] text-[#5b6780]">
                Scope
                <span className="relative">
                  <select
                    value={scope}
                    onChange={e => setScope(e.target.value)}
                    aria-label="Limit answers to one company"
                    className="appearance-none h-8 max-w-[170px] pl-3 pr-8 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md hover:bg-[#f6f7fa] focus:outline-none focus:border-[#1c4ed8] cursor-pointer"
                  >
                    <option value="">All coverage</option>
                    {scopeCompanies.map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                  <ChevronDown size={12} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#76829a]" />
                </span>
              </label>
              {aiModels.length > 0 && (
                <label className="flex items-center gap-1.5 text-[13px] text-[#5b6780]">
                  Model
                  <ModelSelector
                    models={aiModels}
                    selectedModel={selectedAiModel}
                    onSelectModel={onSelectAiModel}
                  />
                </label>
              )}
            </div>
            {hasConversation && activeChatId && (
              <button
                type="button"
                onClick={() => onDeleteChat?.(activeChatId)}
                title="Delete conversation"
                aria-label="Delete conversation"
                className="w-[30px] h-[30px] grid place-items-center rounded-md text-[#45516a] hover:bg-[#f6f7fa] flex-shrink-0 h-8"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>

          {/* Main area */}
          {!hasConversation ? (
            <EmptyChat records={records} onAsk={ask} />
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-5 bg-white">
              {conversation.filter(turn => !turn.greeting).map(turn => (
                <div key={turn.id} className="space-y-3 motion-safe:animate-[aiMessage_240ms_ease-out]">
                  {/* Status notes the app posts itself (e.g. "collection
                      started") have no question - render the answer alone
                      instead of an empty user bubble. */}
                  <div className={`flex items-start gap-2.5 justify-end${turn.question ? '' : ' hidden'}`}>
                    <div className="bg-[#1c4ed8] text-white text-[14.5px] font-medium px-4 py-2.5 rounded-[10px] rounded-br-sm max-w-[78%] leading-relaxed shadow-sm">
                      {turn.displayQuestion || turn.question}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <span className="w-9 h-9 rounded-full bg-white border border-[#c5d3f6] grid place-items-center overflow-hidden">
                        <BotAvatar size={32} decorative className="scale-110" />
                      </span>
                      <span className="text-[15px] font-semibold text-[#0d1524]">CorpSentinel AI</span>
                    </div>
                    <div className="min-w-0 pl-[46px]">
                      {turn.loading && (
                        <div className="text-[13px] text-[#45516a] flex items-center gap-2 py-1">
                          <Loader2 size={14} className="animate-spin text-[#1c4ed8]" />
                          Searching your coverage and drafting a cited answer…
                        </div>
                      )}
                      {turn.error && (
                        <p className="text-[13px] text-[#bd2f2b] flex items-center gap-1.5 py-1">
                          <AlertCircle size={14} /> {turn.error}
                        </p>
                      )}
                      {turn.answer && (() => {
                        const seen = new Set();
                        const uniqueSources = (turn.sources || []).filter(src => {
                          const key = sourceTitle(src) || src?.url;
                          if (!key || seen.has(key)) return false;
                          seen.add(key);
                          return true;
                        });
                        return (
                          <div className="py-1">
                            <div className="space-y-3.5 max-w-3xl">
                              {formatAnswer(turn.answer).map((block, index) => {
                                if (block.type === 'heading') {
                                  return (
                                    <h4 key={index} className="flex items-center gap-2 pt-1 text-base font-semibold text-[#0d1524]">
                                      <span className="w-1 h-4 rounded-full bg-[#1c4ed8] flex-none" />
                                      {renderInlineMarkdown(block.text)}
                                    </h4>
                                  );
                                }
                                if (block.type === 'table') {
                                  return (
                                    <div key={index} className="overflow-x-auto rounded-md border border-[#e1e5ec]">
                                      <table className="w-full text-[13.5px] border-collapse">
                                        <thead>
                                          <tr className="bg-[#f6f7fa]">
                                            {block.head.map((cell, c) => (
                                              <th key={c} className="px-3 py-2 text-left font-semibold text-[#0d1524] border-b border-[#e1e5ec] whitespace-nowrap">
                                                {renderInlineMarkdown(cleanItemText(cell))}
                                              </th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {block.rows.map((row, rIdx) => (
                                            <tr key={rIdx} className="border-t border-[#e1e5ec] align-top">
                                              {row.map((cell, c) => (
                                                <td key={c} className={`px-3 py-2 text-[#2b3750] ${c === 0 ? 'font-medium text-[#0d1524]' : ''}`}>
                                                  <InlineContent text={cleanItemText(cell)} citationNumbers={extractCitations(cell)} sources={turn.sources} />
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                }
                                if (block.type === 'list') {
                                  const ListTag = block.ordered ? 'ol' : 'ul';
                                  return (
                                    <ListTag
                                      key={index}
                                      className={`text-[14.5px] leading-[1.7] text-[#2b3750] space-y-2 pl-5 marker:text-[#1c4ed8] marker:font-semibold ${
                                        block.ordered ? 'list-decimal' : 'list-disc'
                                      }`}
                                    >
                                      {block.items.map((item, itemIndex) => (
                                        <li key={itemIndex} className="pl-1">
                                          <InlineContent
                                            text={item.text}
                                            citationNumbers={item.citationNumbers}
                                            sources={turn.sources}
                                          />
                                        </li>
                                      ))}
                                    </ListTag>
                                  );
                                }
                                const isAction = /^action item:/i.test(block.text);
                                if (isAction) {
                                  return (
                                    <div key={index} className="rounded-md border border-[#c5d3f6] bg-[#e7edfc] px-4 py-3">
                                      <b className="block mb-1 text-[13px] font-semibold text-[#1c4ed8]">Suggested next step</b>
                                      <div className="text-[14px] leading-relaxed text-[#0d1524]">
                                        <InlineContent
                                          text={block.text.replace(/^action item:\s*/i, '')}
                                          citationNumbers={block.citationNumbers}
                                          sources={turn.sources}
                                        />
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={index} className="text-[14.5px] leading-[1.7] text-[#2b3750]">
                                    <InlineContent
                                      text={block.text}
                                      citationNumbers={block.citationNumbers}
                                      sources={turn.sources}
                                    />
                                  </div>
                                );
                              })}
                            </div>

                            {uniqueSources.length > 0 && (
                              <div className="mt-5">
                                <p className="text-[13px] font-semibold text-[#0d1524] mb-2">
                                  Based on {uniqueSources.length} {uniqueSources.length === 1 ? 'source' : 'sources'}
                                </p>
                                <div className="rounded-md border border-[#e1e5ec] overflow-hidden">
                                  {uniqueSources.slice(0, 5).map((s, i) => (
                                    <a
                                      key={s.id || i}
                                      href={s.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={sourceTitle(s)}
                                      className="group flex items-center gap-3 px-3 py-2.5 border-t border-[#e1e5ec] first:border-t-0 text-left transition hover:bg-[#f6f7fa]"
                                    >
                                      <span className="w-6 h-6 rounded bg-[#e7edfc] text-[#1c4ed8] text-xs font-semibold grid place-items-center flex-shrink-0">
                                        {s.n || i + 1}
                                      </span>
                                      <span className="min-w-0 flex-1">
                                        <span className="block text-[13px] font-medium text-[#0d1524] truncate group-hover:text-[#1c4ed8]">
                                          {sourceTitle(s)}
                                        </span>
                                        <span className="block text-xs text-[#76829a] truncate">{sourceDomain(s)}</span>
                                      </span>
                                      <ExternalLink size={13} className="flex-shrink-0 text-[#76829a] group-hover:text-[#1c4ed8]" />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                              {(turn.followUps || []).length > 0 && (
                                <span className="text-[13px] font-medium text-[#5b6780]">Ask next:</span>
                              )}
                              {(turn.followUps || []).map(followUp => (
                                <button
                                  key={followUp.label}
                                  type="button"
                                  onClick={() => askFollowUp(turn, followUp)}
                                  disabled={hasPendingTurn}
                                  className="text-[13px] font-medium text-[#0d1524] hover:text-[#1c4ed8] bg-white border border-[#cbd3df] hover:border-[#1c4ed8] rounded-md px-3 transition disabled:opacity-40 h-8"
                                >
                                  {followUp.label}
                                </button>
                              ))}
                              <span className="ml-auto flex items-center gap-3">
                                {turn.model && <span className="text-xs text-[#76829a]">Answered by {modelLabel(turn.model)}</span>}
                                <CopyButton text={turn.answer} />
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          )}

          {/* Composer — pill style like screenshot */}
          <div className="flex-shrink-0 px-[18px] py-3 border-t border-[#e1e5ec] bg-white">
            <form onSubmit={handleSubmit}>
              <div className="flex items-end gap-2.5 rounded-lg border border-[#cbd3df] bg-white px-3 py-2 focus-within:border-[#1c4ed8] transition">
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={aiPageInput}
                  onChange={e => setAiPageInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder="Ask anything about companies, risks, or regulations…"
                  className="flex-1 resize-none px-1 py-1.5 text-[13.5px] text-[#0d1524] bg-transparent border-0 focus:outline-none placeholder:text-[#76829a] min-h-[32px] max-h-28 leading-relaxed"
                />
                <button
                  type="submit"
                  disabled={!aiPageInput.trim() || hasPendingTurn}
                  className="h-8 px-3 rounded-md bg-[#1c4ed8] hover:brightness-110 text-white text-[13px] font-medium flex items-center gap-1.5 transition disabled:opacity-40 flex-shrink-0"
                >
                  {hasPendingTurn ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Check size={15} />
                  )}
                  Send
                </button>
              </div>
              <p className="text-[11.5px] text-[#76829a] mt-2">
                Answers are drawn from your monitored coverage only. AI can make mistakes: check the cited sources before acting.
                {' '}Enter to send · Shift+Enter for newline.
              </p>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AiPage;
